/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-07-11
 */

import React, { useEffect, useState } from "react";

import { DeceasedPerson } from "../components/DeceasedPerson";
import { DeceasedInformation } from "../components/DeceasedInformation";
import { DeceasedTombstone } from "../components/DeceasedTombstone";
import { DeceasedMemorialWallPosts } from "../components/DeceasedMemorialWallPosts";
import { DeceasedTreeOfLife } from "../components/DeceasedTreeOfLife";
import { MonthlyContributions } from "../components/MonthlyContributions";

import { MessageModal } from "../components/modals/MessageModal";
import { StoneModal } from "../components/modals/StoneModal";
import { SubmitPictureModal } from "../components/modals/SubmitPictureModal";
import { SubmitTombstoneModal } from "../components/modals/SubmitTombstoneModal";
import { YahrzeitReminderModal } from "../components/modals/YahrzeitReminderModal";
import { useParams } from "react-router-dom";
import { fetchRecords } from "../utils/AppUtil";
import { Helmet } from "react-helmet";


type DeceasedType = {
    deceasedId: string,
    title: string,
    firstName: string,
    lastName: string,
    middleName: string,
    hebrewName: string,
    tribe: string,
    gender: string,
    birthDate: string,
    birthDateDisplay: string,
    deathDate: string,
    deathDateDisplay: string,
    burialDateDisplay: string,
    burialTimeDisplay: string,
    consecrationDateDisplay: string,
    consecrationTimeDisplay: string,
    yahrzeitDateDisplay: string,
    yahrzeitDesc: string,
    cemeteryName: string,
    synagogueName: string,
    synagogueEmail: string,
    locationDesc: string,
    locationNumber: string,
    ashesDesc: string,
    metaTagTitle: string,
    metaTagDescription: string,
    metaTagKeywords: string
}

export const Deceased: React.FC = () => {

    type DeceasedParamsType = { deceasedId: string };
    const params: DeceasedParamsType = useParams();
    const endpoint = `deceased/${params.deceasedId}`;
    const [stoneRefresh, setStoneRefresh] = useState(0);

    const deceasedData: DeceasedType = {
        deceasedId: "",
        title: "",
        firstName: "",
        lastName: "",
        middleName: "",
        hebrewName: "",
        tribe: "",
        gender: "",
        birthDate: "",
        birthDateDisplay: "",
        deathDate: "",
        deathDateDisplay: "",
        burialDateDisplay: "",
        burialTimeDisplay: "",
        consecrationDateDisplay: "",
        consecrationTimeDisplay: "",
        yahrzeitDateDisplay: "",
        yahrzeitDesc: "",
        cemeteryName: "",
        synagogueName: "",
        synagogueEmail: "",
        locationDesc: "",
        locationNumber: "",
        ashesDesc: "",
        metaTagTitle: "",
        metaTagDescription: "",
        metaTagKeywords: ""
    }
    const [deceased, setDeceased] = useState<DeceasedType>(deceasedData);
    const processData = (responseJson: DeceasedType) => {
        setDeceased(responseJson);
    }

    // Do not change this or put any additional code in here without checking with
    // the team first.
    useEffect(() => {

        fetchRecords(endpoint, processData)
        .catch((error) => {
            console.log("Error : " + error)
        })

    }, [endpoint]);

    const refresh = () => {
        setStoneRefresh(stoneRefresh + 1);
        return stoneRefresh;
    }

    

    return(
        <>
            <Helmet>
                <title>{deceased.metaTagTitle ? deceased.metaTagTitle : deceased.firstName + " " + deceased.lastName}  - Jewish Cemetery</title>
                <meta name="description" content={deceased.metaTagDescription ? deceased.metaTagDescription : "This website was designed by the Jewish Cemeteries Maintenance Board to pay tribute to loved ones that are no longer with us."} />
            </Helmet>
            
            <div className="jc-grid">
                <div className="row">

                    <div className="col-md-12 page-title">
                        <DeceasedPerson 
                            deceasedId={params.deceasedId} 
                            firstName={deceased.firstName} 
                            lastName={deceased.lastName} 
                            yahrzeitDateDisplay={deceased.yahrzeitDateDisplay} 
                            yahrzeitDesc={deceased.yahrzeitDesc}  
                        />
                    </div>

                    <div className="col-lg-4 col-md-6">
                        <DeceasedInformation />
                    </div>

                    <div className="col-lg-4 col-md-6">
                        <DeceasedTombstone deceasedId={params.deceasedId} firstName={deceased.firstName} lastName={deceased.lastName} birthDateDisplay={deceased.birthDateDisplay} deathDateDisplay={deceased.deathDateDisplay} stoneRefresh={stoneRefresh} />
                    </div>

                    <div className="col-lg-4 col-md-6">
                        <DeceasedMemorialWallPosts endpoint={"wallpost/deceased"} deceasedId={params.deceasedId} recordCount={30} />
                    </div>

                    <div className="col-md-6">
                        <DeceasedTreeOfLife endpoint={"leaf/deceased"} deceasedId={params.deceasedId} />
                    </div>
                    
                    <div className="col-lg-6">
                        <MonthlyContributions endpoint={"transactions/summary/monthToDate"} />
                    </div>

                </div>
            </div>

            <MessageModal 
                endpoint={"wallpost"}
                deceasedId={params.deceasedId}
                firstName={deceased.firstName}
                lastName={deceased.lastName}
            />

            <StoneModal 
                endpoint={"stone"}
                deceasedId={params.deceasedId}
                firstName={deceased.firstName}
                lastName={deceased.lastName}
                refresh={refresh}
             />

            <SubmitPictureModal 
                endpoint={"pictures/create"}
                deceasedId={params.deceasedId}
                firstName={deceased.firstName}
                lastName={deceased.lastName}
            />

            <SubmitTombstoneModal 
                endpoint={"pictures/create"}
                deceasedId={params.deceasedId}
                firstName={deceased.firstName}
                lastName={deceased.lastName}
            />

            <YahrzeitReminderModal 
                endpoint={"yahrzeit"}
                deceasedId={params.deceasedId}
                firstName={deceased.firstName}
                lastName={deceased.lastName}
            />
        </>
    );
}